import { getUrl, uploadData, list } from 'aws-amplify/storage';
import { Injectable } from '@angular/core';
import { FileTypeEnum, GetAssessmentStandardFrameworkQuery } from 'app/API.service';
import { FileService } from 'app/shared/file.service';
import { UtilsService } from 'app/shared/utils.service';

@Injectable({
  providedIn: 'root',
})
export class FrameworkTreeService {
  cache = {};
  //* Cache to store assessment trees based on assessmentId, frameworkKey and type (group or standard)
  private treeCache = new Map<string, any>();

  constructor(private fileService: FileService) {}

  //* This function will first check if the requested tree is already in cache,
  //* if yes then it will return the cached tree, if not then it will fetch the tree from S3, store it in cache and then return it.
  async getAssessmentTreeWithCache(assessmentId: string, frameworkKey: string, isGroup: boolean = false): Promise<any> {
    const typeKey = isGroup ? 'GROUP' : 'STD';
    const cacheKey = `${assessmentId}#${frameworkKey}#${typeKey}`;

    if (this.treeCache.has(cacheKey)) {
      return JSON.parse(JSON.stringify(this.treeCache.get(cacheKey)));
    }

    let data;
    if (isGroup) {
      data = await this.getGroupAssessmentTreeFromS3(assessmentId, { key: frameworkKey } as any);
    } else {
      data = await this.getAssessmentTreeFromS3(assessmentId, { key: frameworkKey } as any);
    }

    if (data) {
      this.treeCache.set(cacheKey, data);
      return JSON.parse(JSON.stringify(data));
    }
    return null;
  }

  //* This function will clear the cached trees for a specific assessment,
  // *it can be called when there is an update in the assessment tree to ensure that next time fresh data is fetched from S3.
  clearCacheForAssessment(assessmentId: string): void {
    if (!assessmentId) {
      return;
    }
    const keysToDelete = [];
    this.treeCache.forEach((value, key) => {
      if (key.startsWith(`${assessmentId}#`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.treeCache.delete(key));
  }

  async getFrameworkFromS3(frameworkName: string): Promise<any> {
    try {
      const filePath = `FRAMEWORKS/${frameworkName}.csv`;
      return this.fileService.getTextFileFromS3(filePath, { download: true, level: 'public' });
    } catch (err) {
      console.log('ERR: getFrameworkFromS3 - :', err);
      return null;
    }
  }

  /**
   * TODO: Have to handle encryption checks
   * BECAUSE: unclear about its flow, since its path is currently
   * NOT in encrypted files, so its left unchanged for now.
   */
  getFrameworkTreeFromS3(frameworkKey: string): Promise<any> {
    try {
      const filePath = `${FileTypeEnum.FRAMEWORK_TREES}/${frameworkKey}.json`;
      // const options: any = { download: true, contentType: 'application/json', level: 'public' };
      return (this.cache[frameworkKey] = getUrl({ path: filePath }));
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  /**
   *
   * @param frameworkName
   * @returns framework tree file from S3
   */

  async getFrameworkTree(frameworkKey: string): Promise<any> {
    try {
      const filePath = `public/${FileTypeEnum.FRAMEWORK_TREES}/${frameworkKey}.json`;
      return this.fileService.getJSONFileFromS3(filePath, {
        download: true,
        Recursive: true,
        contentType: 'application/json',
        level: 'public',
        cacheControl: 'no-cache',
      });
    } catch (err) {
      console.log('ERR: getFrameworkTree - :', err);
      return null;
    }
  }

  async getAssessmentTreeFromS3(assessmentId: string, framework: GetAssessmentStandardFrameworkQuery): Promise<any> {
    try {
      const filePath = `${FileTypeEnum.ASSESSMENTS}/${assessmentId}/${framework.key}.json`;

      return this.fileService.getJSONFileFromS3(filePath, {
        download: true,
        Recursive: true,
        contentType: 'application/json',
        level: 'public',
        cacheControl: 'no-cache',
      });
    } catch (err) {
      console.log('ERR: getAssessmentTreeFromS3 - :', err);
      return null;
    }
  }

  async getArchivedAssessmentTreeFromS3(
    assessmentId: string,
    framework: GetAssessmentStandardFrameworkQuery
  ): Promise<any> {
    try {
      const filePath = `${FileTypeEnum.ASSESSMENTS}/${assessmentId}/ARCHIVED/${framework.key}.json`;

      return this.fileService.getJSONFileFromS3(filePath, {
        download: true,
        Recursive: true,
        contentType: 'application/json',
        level: 'public',
        cacheControl: 'no-cache',
      });
    } catch (err) {
      console.log('ERR:: getArchivedAssessmentTreeFromS3 - ', err);
      return null;
    }
  }

  async getTasksListTreeFromS3(assessmentId: string, framework: GetAssessmentStandardFrameworkQuery): Promise<any> {
    try {
      const filePath = `${FileTypeEnum.ASSESSMENTS}/${assessmentId}/${framework.key}/TASKS/TASKS.json`;

      return this.fileService.getJSONFileFromS3(filePath, {
        download: true,
        contentType: 'application/json',
        level: 'public',
      });
    } catch (err) {
      console.log('ERR:: getTasksListTreeFromS3 - ', err);
      return null;
    }
  }

  async getGroupAssessmentTreeFromS3(
    assessmentId: string,
    framework: GetAssessmentStandardFrameworkQuery
  ): Promise<any> {
    try {
      const filePath = `${FileTypeEnum.ASSESSMENTS}/${assessmentId}/${framework.key}/GROUPS/GROUPS.json`;

      return this.fileService.getJSONFileFromS3(filePath, {
        download: true,
        contentType: 'application/json',
        level: 'public',
        cacheControl: 'no-cache',
      });
    } catch (err) {
      console.log('ERR:: getGroupAssessmentTreeFromS3 - ', err);
      return null;
    }
  }
  async getDeniedDataFromS3(assessmentId: string): Promise<any> {
    try {
      const filePath = `${FileTypeEnum.ASSESSMENTS}/${assessmentId}/deniedList.json`;
      const { items } = await list({
        path: filePath,
        options: {
          listAll: true,
        },
      });
      if (items.length) {
        return this.fileService.getJSONFileFromS3(filePath, {
          download: true,
          contentType: 'application/json',
          level: 'public',
          cacheControl: 'no-cache',
        });
      } else {
        return [];
      }
    } catch (err) {
      console.log('ERR:: getDeniedDataFromS3 - ', err);
      return [];
    }
  }

  async getArtifactDataFromS3(assessmentId: string): Promise<any> {
    try {
      const filePath = `${FileTypeEnum.ASSESSMENTS}/${assessmentId}/artifactList.json`;
      const { items } = await list({
        path: filePath,
        options: {
          listAll: true,
        },
      });
      if (items.length) {
        return this.fileService.getJSONFileFromS3(filePath, {
          download: true,
          contentType: 'application/json',
          level: 'public',
          cacheControl: 'no-cache',
        });
      } else {
        return [];
      }
    } catch (err) {
      console.log('ERR:: getArtifactDataFromS3 - ', err);
      return [];
    }
  }

  // write:: have to update
  async setDeniedListToS3(assessmentId, s3File): Promise<any> {
    const config: any = { contentType: s3File.contentType };
    config.level = 'public';
    config.download = true;
    try {
      const filePath = `${FileTypeEnum.ASSESSMENTS}/${assessmentId}/deniedList.json`;
      const result = await uploadData({ path: filePath, data: s3File.body }).result;
      s3File.key = result.path;
      delete s3File.body;
      return s3File;
    } catch (err) {
      console.log('error -setReportInfoToS3: ', err);
      return null;
    }
  }

  // write:: have to update
  async setArtifactToS3(assessmentId, s3File): Promise<any> {
    const config: any = { contentType: s3File.contentType };
    config.level = 'public';
    config.download = true;
    try {
      const filePath = `${FileTypeEnum.ASSESSMENTS}/${assessmentId}/artifactList.json`;
      const result = await uploadData({ path: filePath, data: s3File.body }).result;
      s3File.key = result.path;
      delete s3File.body;
      return s3File;
    } catch (err) {
      console.log('error -setReportInfoToS3: ', err);
      return null;
    }
  }
  /**
   *
   * @param assessmentId
   * @param s3File
   * @param frameworkName
   * @returns UPDATE FRAMEWORK TREE IN S3
   */
  async setFrameworkTreeToS3(assessmentId, s3File, frameworkName = ''): Promise<any> {
    const config: any = { contentType: 'application/json' };
    config.level = 'public';
    config.download = true;
    try {
      const filePath = `${FileTypeEnum.ASSESSMENTS}/${assessmentId}/${frameworkName}.json`;
      const result = await uploadData({ path: filePath, data: s3File.body }).result;
      s3File.key = result.path;
      delete s3File?.body;
      return s3File;
    } catch (err) {
      console.log('error -setReportInfoToS3: ', err);
      return null;
    }
  }

  async getAssessmentRevisionTreeFromS3(id: string, frameworkName: string, date: string): Promise<any> {
    try {
      const filePath = `public/${FileTypeEnum.REVISIONS}/${date}/${FileTypeEnum.ASSESSMENTS}/${id}/${frameworkName}.json`;
      const isFile = await this.fileService.checkIfPathExistsS3(filePath);
      if (isFile && isFile.length) {
        return this.fileService.getJSONFileFromS3(filePath, {
          download: true,
          contentType: 'application/json',
          level: 'public',
        });
      }
      return null;
    } catch (err) {
      console.log('Error fetching file: ', err);
      return null;
    }
  }

  async getAssessmentControlsFromS3(assessmentId: string): Promise<any> {
    try {
      const filePath = `${FileTypeEnum.ASSESSMENTS}/${assessmentId}/controls.json`;

      return this.fileService.getJSONFileFromS3(filePath, {
        download: true,
        contentType: 'application/json',
        level: 'public',
      });
    } catch (err) {
      console.log('ERR: getAssessmentControlsFromS3 - ', err);
      return null;
    }
  }

  flatTree(frameworkTree) {
    // This will get the controls from frameworkTree by flattening the tree
    const leafNodes = [];
    function traverse(node) {
      if (!node.children || node.children.length === 0) {
        leafNodes.push(node);
      } else {
        for (const child of node.children) {
          traverse(child);
        }
      }
    }

    traverse(frameworkTree);
    return leafNodes;
  }

  async getControlsFromAssessment(assessmentId: string, framework): Promise<any> {
    try {
      const frameworkTree = await this.getAssessmentTreeFromS3(assessmentId, framework);
      const controls = this.flatTree(frameworkTree);
      return { controls };
    } catch (err) {
      return {};
    }
  }

  static getFrameworkKey(): string {
    let frameKey = 'NIST_CSF';
    switch (true) {
      case UtilsService.isBnB:
        frameKey = 'NIST_CSF_BB';
        break;
      case UtilsService.isBnBCyberSite:
        frameKey = 'PARAGON_FULL';
        break;
      case UtilsService.isOklahomaUniversity:
        frameKey = 'NIST_OU';
        break;
      case UtilsService.isITC:
        frameKey = 'ITC_CUSTOM';
        break;
    }

    return frameKey;
  }

  async getRiskControlsFromS3(fileName: string, frameworkKey: string = 'NIST_CSF'): Promise<any> {
    try {
      const filePath = `RISKS/${fileName}-${
        frameworkKey.includes('#') ? frameworkKey.split('#')[0] : frameworkKey
      }.json`;
      const res = await this.fileService.getJSONFileFromS3(filePath, {
        download: true,
        contentType: 'application/json',
        level: 'public',
      });
      return res;
    } catch (err) {
      console.warn('Info: getRiskControlsFromS3 - ', err);
      return null;
    }
  }
}
